import { useCallback, useRef } from 'react';

interface UseAutoScrollOptions {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  isDraggingRef: React.MutableRefObject<boolean>;
  mouseYRef: React.MutableRefObject<number | null>;
  dragDirectionRef?: React.MutableRefObject<'up' | 'down' | null>;
}

export const useAutoScroll = ({
  scrollContainerRef,
  isDraggingRef,
  mouseYRef,
  dragDirectionRef,
}: UseAutoScrollOptions) => {
  const autoScrollIntervalRef = useRef<number | null>(null);

  const startAutoScroll = useCallback(() => {
    if (!scrollContainerRef.current) {
      return;
    }

    if (autoScrollIntervalRef.current) {
      return;
    }

    const SCROLL_SPEED = 20;
    const SCROLL_INTERVAL = 16;

    autoScrollIntervalRef.current = window.setInterval(() => {
      if (!scrollContainerRef.current) {
        if (autoScrollIntervalRef.current) {
          window.clearInterval(autoScrollIntervalRef.current);
          autoScrollIntervalRef.current = null;
        }
        return;
      }

      if (!isDraggingRef.current) {
        if (autoScrollIntervalRef.current) {
          window.clearInterval(autoScrollIntervalRef.current);
          autoScrollIntervalRef.current = null;
        }
        return;
      }

      const container = scrollContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const mouseY = mouseYRef.current;

      if (mouseY === null) {
        return;
      }

      const currentDragDirection = dragDirectionRef?.current ?? null;

      const isScrollable = container.scrollHeight > container.clientHeight;
      const visibleTop = containerRect.top;
      const visibleBottom = containerRect.top + container.clientHeight;
      const mouseIsBelowVisible = mouseY > visibleBottom;
      const mouseIsAboveVisible = mouseY < visibleTop;

      if (!isScrollable && !mouseIsBelowVisible && !mouseIsAboveVisible) {
        return;
      }

      const distanceFromTop = mouseY - visibleTop;
      const distanceFromBottom = visibleBottom - mouseY;

      // Use dynamic threshold - 45% of visible height, minimum 150px
      // This ensures scroll triggers when user is in the lower/upper half of the container
      const visibleHeight = visibleBottom - visibleTop;
      const SCROLL_THRESHOLD = Math.max(150, visibleHeight * 0.45);

      const isNearTop = distanceFromTop < SCROLL_THRESHOLD || mouseY < visibleTop;
      const isNearBottom = distanceFromBottom < SCROLL_THRESHOLD || mouseY > visibleBottom;

      // Scroll up - only if dragging upward or no direction determined yet
      const shouldScrollUp =
        isNearTop && (currentDragDirection === 'up' || currentDragDirection === null);
      if (shouldScrollUp && container.scrollTop > 0) {
        const scrollAmount = Math.min(SCROLL_SPEED, container.scrollTop);
        container.scrollBy({ top: -scrollAmount, behavior: 'auto' });
      }

      // Check for parent scroll when at top - only if dragging up
      const isAtInnerTop = container.scrollTop <= 0;
      if ((isAtInnerTop || container.scrollTop <= 0) && shouldScrollUp) {
        let parent: HTMLElement | null = container.parentElement;
        let scrolledParent = false;
        let parentCheckCount = 0;

        while (
          parent &&
          parent !== document.body &&
          parent !== document.documentElement &&
          parentCheckCount < 10
        ) {
          parentCheckCount++;
          const parentScrollHeight = parent.scrollHeight;
          const parentClientHeight = parent.clientHeight;
          const parentRemainingScrollUp = parent.scrollTop;
          const isParentScrollable = parentScrollHeight > parentClientHeight;
          const parentStyle = window.getComputedStyle(parent);
          const hasOverflow =
            parentStyle.overflowY === 'auto' ||
            parentStyle.overflowY === 'scroll' ||
            isParentScrollable;

          if (hasOverflow && parentRemainingScrollUp > 0) {
            const parentScrollAmount = Math.min(SCROLL_SPEED, parentRemainingScrollUp);
            parent.scrollBy({ top: -parentScrollAmount, behavior: 'auto' });
            scrolledParent = true;
            break;
          }
          parent = parent.parentElement;
        }

        if (!scrolledParent) {
          const windowRemainingScrollUp = window.scrollY;
          if (windowRemainingScrollUp > 0) {
            const windowScrollAmount = Math.min(SCROLL_SPEED, windowRemainingScrollUp);
            window.scrollBy({ top: -windowScrollAmount, behavior: 'auto' });
          }
        }
      }

      // Scroll down - only if dragging downward or no direction determined yet
      const shouldScrollDown =
        isNearBottom && (currentDragDirection === 'down' || currentDragDirection === null);

      if (shouldScrollDown) {
        const currentMaxScroll = container.scrollHeight - container.clientHeight;
        const currentRemainingScroll = currentMaxScroll - container.scrollTop;

        if (currentRemainingScroll > 0) {
          if (currentRemainingScroll <= 10) {
            container.scrollTop = currentMaxScroll;
          } else {
            const distanceBelow = mouseY - visibleBottom;
            const speedMultiplier = distanceBelow > 100 ? 1.5 : 1;
            const baseScrollAmount = Math.min(
              SCROLL_SPEED * speedMultiplier,
              currentRemainingScroll
            );
            const scrollAmount = Math.max(1, baseScrollAmount);

            container.scrollBy({ top: scrollAmount, behavior: 'auto' });

            const newMaxScroll = container.scrollHeight - container.clientHeight;
            const newScrollTop = container.scrollTop;
            const newRemainingScroll = newMaxScroll - newScrollTop;

            if (newRemainingScroll > 0 && newRemainingScroll <= 5) {
              container.scrollTop = newMaxScroll;
            } else if (newScrollTop > newMaxScroll) {
              container.scrollTop = newMaxScroll;
            }
          }
        }

        const finalMaxScroll = container.scrollHeight - container.clientHeight;
        const finalRemainingScroll = finalMaxScroll - container.scrollTop;

        if (finalRemainingScroll > 0 && finalRemainingScroll <= 5) {
          container.scrollTop = finalMaxScroll;
        }

        const isAtInnerBottom = container.scrollTop >= finalMaxScroll;
        const cannotScrollInner = currentRemainingScroll <= 0;

        if ((isAtInnerBottom || cannotScrollInner) && shouldScrollDown) {
          let parent: HTMLElement | null = container.parentElement;
          let scrolledParent = false;
          let parentCheckCount = 0;

          while (
            parent &&
            parent !== document.body &&
            parent !== document.documentElement &&
            parentCheckCount < 10
          ) {
            parentCheckCount++;
            const parentScrollHeight = parent.scrollHeight;
            const parentClientHeight = parent.clientHeight;
            const parentMaxScroll = parentScrollHeight - parentClientHeight;
            const parentRemainingScroll = parentMaxScroll - parent.scrollTop;
            const isParentScrollable = parentScrollHeight > parentClientHeight;
            const parentStyle = window.getComputedStyle(parent);
            const hasOverflow =
              parentStyle.overflowY === 'auto' ||
              parentStyle.overflowY === 'scroll' ||
              isParentScrollable;

            if (hasOverflow && parentRemainingScroll > 0) {
              const parentScrollAmount = Math.min(SCROLL_SPEED, parentRemainingScroll);
              parent.scrollBy({ top: parentScrollAmount, behavior: 'auto' });
              scrolledParent = true;
              break;
            }
            parent = parent.parentElement;
          }

          if (!scrolledParent) {
            const windowMaxScroll = document.documentElement.scrollHeight - window.innerHeight;
            const windowRemainingScroll = windowMaxScroll - window.scrollY;
            if (windowRemainingScroll > 0) {
              const windowScrollAmount = Math.min(SCROLL_SPEED, windowRemainingScroll);
              window.scrollBy({ top: windowScrollAmount, behavior: 'auto' });
            }
          }
        }
      }
    }, SCROLL_INTERVAL);
  }, [scrollContainerRef, isDraggingRef, mouseYRef, dragDirectionRef]);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollIntervalRef.current) {
      window.clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  }, []);

  return { startAutoScroll, stopAutoScroll };
};
